import Cropper from 'cropperjs';
import * as rectLib from '../../../../../../common/maths/geometry/rect';
import { CapacitorHttp } from '@capacitor/core';
import { getCurrentUserToken } from '../../../../../auth/authService';
import { getImageEditorData } from '../../imageModalUtils';
import React from 'react';
import { asObject } from '../../../../../../common/utils/immutableHelper';
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

export const positionImageInCanvasCenter = (cropper: Cropper) => {
    const { width, height } = cropper.getContainerData();
    const { naturalWidth, naturalHeight } = cropper.getImageData();

    const containerSize = Math.min(width, height);
    const imageSize = Math.min(naturalWidth, naturalHeight);

    const zoomScale = containerSize / imageSize;

    cropper.zoomTo(zoomScale, {
        x: width / 2,
        y: height / 2,
    });
};

export const fetchImageUsingCapacitorHTTP = async (
    imageUrl: string,
    imageType: string,
): Promise<{ blob: Blob; objectUrl: string }> => {
    const token = getCurrentUserToken();
    const response = await CapacitorHttp.get({
        url: imageUrl,
        responseType: 'arraybuffer',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    const base64Data = response.data;
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);

    const blob = new Blob([byteArray], { type: imageType });
    const objectUrl = URL.createObjectURL(blob);

    return { blob, objectUrl };
};

export const loadImageIntoCanvas = (
    objectUrl: string,
    imageType: string,
): Promise<{ img: HTMLImageElement; dataUrl: string }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            if (!ctx) return null;
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL(imageType);

            resolve({ img, dataUrl });
        };

        img.onerror = reject;
        img.src = objectUrl;
        img.alt = 'To edit';
    });
};

export const loadCanvasIntoCropper = (
    imageRef: React.RefObject<HTMLImageElement>,
    cropperRef: React.MutableRefObject<Cropper | null>,
    element: ImMNElement,
    setEditorIsReady: (read: boolean) => void,
    isDirtyRef: React.MutableRefObject<boolean>,
) => {
    const imageEditorData = getImageEditorData(element) && asObject(getImageEditorData(element));

    cropperRef.current = new Cropper(imageRef.current as HTMLImageElement, {
        dragMode: 'none',
        viewMode: 2,
        center: false,
        movable: false,
        zoomOnTouch: false,
        zoomOnWheel: false,
        toggleDragModeOnDblclick: false,
        autoCrop: true,
        autoCropArea: 1,
        checkOrientation: false,
        ready: () => {
            if (cropperRef.current && imageEditorData) {
                const cropper = cropperRef.current;
                const initialSelectionData = {
                    x: imageEditorData.x,
                    y: imageEditorData.y,
                    width: imageEditorData.width,
                    height: imageEditorData.height,
                };
                cropper.clear();
                cropper.setData(imageEditorData);
                cropper.crop();
                cropper.setData(initialSelectionData);
                positionImageInCanvasCenter(cropper);
            }

            setEditorIsReady(true);
        },
        cropmove: () => {
            isDirtyRef.current = true;
        },
    });
};

export const adjustCropperForRotation = (
    cropper: Cropper,
    modalWidth: number,
    initialCropData: Cropper.CropBoxData,
) => {
    const initialContainerData = cropper.getContainerData();

    // The modal might change scale when it gets rotated due to the a wide or tall aspect ratio.
    // We need to find out the scale change so that we can convert the initial crop rectangle into
    // the final crop rectangle
    const scalingFactor = modalWidth / rectLib.getHeight(initialContainerData);
    // We're assigning the Cropper object as 'any' because the 'render()' function isn't publicly
    // exposed but does exist, and needs to be called to ensure the new rotation has been rendered
    // before we can update the selection element
    (cropper as any).render();

    let newCropRect = {
        top: initialContainerData.width - initialCropData.left - initialCropData.width,
        left: initialCropData.top,
        width: initialCropData.height,
        height: initialCropData.width,
    };
    newCropRect = rectLib.asRect(newCropRect);
    // @ts-ignore - rectlib.scale uses lodash curry, which breaks the type inference
    newCropRect = rectLib.scale(scalingFactor, newCropRect);

    cropper.setCropBoxData({
        top: rectLib.getTop(newCropRect),
        left: rectLib.getLeft(newCropRect),
        width: rectLib.getWidth(newCropRect),
        height: rectLib.getHeight(newCropRect),
    });
};
